import React, { useState } from 'react';
import { 
  Card, 
  Button, 
  Typography, 
  Row, 
  Col, 
  Alert, 
  Progress, 
  Space,
  Divider,
  Tag,
  Statistic,
  message,
  Modal,
  Table,
  Descriptions,
  Switch,
  List,
  Badge,
  Tooltip
} from 'antd';
import {
  DatabaseOutlined,
  PlayCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  DollarCircleOutlined,
  EnvironmentOutlined,
  CalendarOutlined,
  PercentageOutlined,
  NumberOutlined
} from '@ant-design/icons';
import { taxRulesService } from '../../services/taxRulesService';
import { exciseTaxRulesSeed, seedDataSummary } from '../../data/exciseTaxRulesSeed';
import './ExciseTaxSeed.css';

const { Title, Text, Paragraph } = Typography;

const ExciseTaxSeed = () => {
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [seedingComplete, setSeedingComplete] = useState(false);
  const [seedingResults, setSeedingResults] = useState(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [selectedRules, setSelectedRules] = useState(new Set(exciseTaxRulesSeed.map(rule => rule.id)));

  const handleSeedData = async () => {
    setLoading(true);
    setProgress(0);
    setSeedingComplete(false);
    setSeedingResults(null);

    try {
      const rulesToSeed = exciseTaxRulesSeed.filter(rule => selectedRules.has(rule.id));
      const total = rulesToSeed.length;
      let successful = 0;
      let failed = 0;
      const errors = [];

      message.info(`Starting to seed ${total} excise tax rules...`);

      for (let i = 0; i < rulesToSeed.length; i++) {
        const rule = rulesToSeed[i];
        setProgress(Math.round((i / total) * 100));

        try {
          await taxRulesService.createRule(rule);
          successful++;
          message.success(`✓ ${rule.name} - ${rule.jurisdiction.state || 'Federal'}`);
        } catch (error) {
          failed++;
          const errorMsg = `Failed to seed ${rule.name}: ${error.message}`;
          errors.push(errorMsg);
          message.error(errorMsg);
        }

        // Small delay to show progress
        await new Promise(resolve => setTimeout(resolve, 300));
      }

      setProgress(100);
      setSeedingComplete(true);
      setSeedingResults({
        total,
        successful,
        failed,
        errors
      });

      if (successful > 0) {
        message.success(`🎉 Successfully seeded ${successful} excise tax rules!`);
      }

    } catch (error) {
      message.error(`Seeding failed: ${error.message}`);
      console.error('Seeding error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRuleToggle = (ruleId, checked) => {
    const newSelected = new Set(selectedRules);
    if (checked) {
      newSelected.add(ruleId);
    } else {
      newSelected.delete(ruleId);
    }
    setSelectedRules(newSelected);
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedRules(new Set(exciseTaxRulesSeed.map(rule => rule.id)));
    } else {
      setSelectedRules(new Set());
    }
  };

  const getMethodColor = (method) => {
    switch (method) {
      case 'specific': return 'blue';
      case 'ad_valorem': return 'green';
      default: return 'default';
    }
  };

  const getRuleTypeIcon = (ruleType) => {
    switch (ruleType) {
      case 'percentage': return <PercentageOutlined style={{ color: '#52c41a' }} />;
      case 'per_unit': return <NumberOutlined style={{ color: '#1890ff' }} />;
      case 'per_gram': return <NumberOutlined style={{ color: '#722ed1' }} />;
      default: return <DollarCircleOutlined />;
    }
  };

  const columns = [
    {
      title: 'Select',
      dataIndex: 'select',
      width: 60,
      render: (_, record) => (
        <Switch
          size="small"
          checked={selectedRules.has(record.id)}
          onChange={(checked) => handleRuleToggle(record.id, checked)}
        />
      )
    },
    {
      title: 'Rule Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <div>
          <strong>{text}</strong>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.description}
          </Text>
        </div>
      )
    },
    {
      title: 'Jurisdiction',
      dataIndex: 'jurisdiction',
      key: 'jurisdiction',
      width: 120,
      render: (jurisdiction) => (
        <div>
          <EnvironmentOutlined /> {jurisdiction.state || 'Federal'}
        </div>
      )
    },
    {
      title: 'Product Category',
      dataIndex: 'taxCategoryPath',
      key: 'category',
      render: (path) => (
        <Tag color="blue">{path.split(' > ').pop()}</Tag>
      )
    },
    {
      title: 'Tax Rate',
      dataIndex: 'rate',
      key: 'rate',
      width: 120,
      render: (rate, record) => (
        <div>
          {getRuleTypeIcon(record.ruleType)}
          <span style={{ marginLeft: 4 }}>
            {record.ruleType === 'percentage' ? `${rate}%` : `$${rate}`}
          </span>
          <br />
          <Text type="secondary" style={{ fontSize: '11px' }}>
            {record.unit}
          </Text>
        </div>
      )
    },
    {
      title: 'Method',
      dataIndex: 'calculationMethod',
      key: 'method',
      width: 100,
      render: (method) => (
        <Tag color={getMethodColor(method)}>
          {method === 'specific' ? 'Specific' : 'Ad Valorem'}
        </Tag>
      )
    },
    {
      title: 'Effective Date',
      dataIndex: 'effectiveDate',
      key: 'effectiveDate',
      width: 110,
      render: (date) => (
        <div>
          <CalendarOutlined />
          <span style={{ marginLeft: 4 }}>
            {new Date(date).toLocaleDateString()}
          </span>
        </div>
      )
    }
  ];

  return (
    <div className="excise-tax-seed">
      <Row gutter={24}>
        <Col span={24}>
          <Card>
            <div style={{ textAlign: 'center', marginBottom: 32 }}>
              <DatabaseOutlined style={{ fontSize: 48, color: '#1890ff', marginBottom: 16 }} />
              <Title level={2}>Excise Tax Rules Seeding</Title>
              <Paragraph>
                Seed your database with real-world excise tax rules for tobacco and vaping products.
                This will populate the TaxRules collection with current rates from 5 key states.
              </Paragraph>
            </div>

            {/* Summary Statistics */}
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="Total Rules"
                    value={seedDataSummary.totalRules}
                    prefix={<DatabaseOutlined />}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="Jurisdictions"
                    value={Object.keys(seedDataSummary.jurisdictions).length}
                    prefix={<EnvironmentOutlined />}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="Specific Rates"
                    value={seedDataSummary.calculationMethods.specific}
                    prefix={<NumberOutlined />}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="Percentage Rates"
                    value={seedDataSummary.calculationMethods.adValorem}
                    prefix={<PercentageOutlined />}
                  />
                </Card>
              </Col>
            </Row>

            {/* Coverage Details */}
            <Card size="small" style={{ marginBottom: 24 }}>
              <Title level={4}>Coverage Details</Title>
              <Row gutter={16}>
                <Col span={8}>
                  <Descriptions title="Jurisdictions" size="small" column={1}>
                    <Descriptions.Item label="Federal">
                      <Badge count={seedDataSummary.jurisdictions.federal} />
                    </Descriptions.Item>
                    <Descriptions.Item label="California">
                      <Badge count={seedDataSummary.jurisdictions.california} />
                    </Descriptions.Item>
                    <Descriptions.Item label="New York">
                      <Badge count={seedDataSummary.jurisdictions.newYork} />
                    </Descriptions.Item>
                    <Descriptions.Item label="Illinois">
                      <Badge count={seedDataSummary.jurisdictions.illinois} />
                    </Descriptions.Item>
                    <Descriptions.Item label="Connecticut">
                      <Badge count={seedDataSummary.jurisdictions.connecticut} />
                    </Descriptions.Item>
                    <Descriptions.Item label="Rhode Island">
                      <Badge count={seedDataSummary.jurisdictions.rhodeIsland} />
                    </Descriptions.Item>
                    <Descriptions.Item label="Minnesota">
                      <Badge count={seedDataSummary.jurisdictions.minnesota} />
                    </Descriptions.Item>
                  </Descriptions>
                </Col>
                <Col span={8}>
                  <Descriptions title="Product Types" size="small" column={1}>
                    <Descriptions.Item label="Cigarettes">
                      <Badge count={seedDataSummary.productTypes.cigarettes} />
                    </Descriptions.Item>
                    <Descriptions.Item label="Vaping Devices">
                      <Badge count={seedDataSummary.productTypes.vapingDevices} />
                    </Descriptions.Item>
                    <Descriptions.Item label="E-Liquids">
                      <Badge count={seedDataSummary.productTypes.eLiquids} />
                    </Descriptions.Item>
                  </Descriptions>
                </Col>
                <Col span={8}>
                  <Descriptions title="Tax Ranges" size="small" column={1}>
                    <Descriptions.Item label="Cigarettes">
                      <Text code>{seedDataSummary.taxRanges.cigarettes}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Vaping %">
                      <Text code>{seedDataSummary.taxRanges.vapingPercentage}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Vaping per mL">
                      <Text code>{seedDataSummary.taxRanges.vapingPerMl}</Text>
                    </Descriptions.Item>
                  </Descriptions>
                </Col>
              </Row>
            </Card>

            {/* Action Buttons */}
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <Space size="large">
                <Button
                  type="primary"
                  icon={<PlayCircleOutlined />}
                  size="large"
                  loading={loading}
                  onClick={handleSeedData}
                  disabled={selectedRules.size === 0}
                >
                  Seed {selectedRules.size} Rules
                </Button>
                <Button
                  icon={<InfoCircleOutlined />}
                  size="large"
                  onClick={() => setPreviewVisible(true)}
                >
                  Preview Rules
                </Button>
              </Space>
            </div>

            {/* Progress */}
            {loading && (
              <div style={{ marginBottom: 24 }}>
                <Progress
                  percent={progress}
                  status="active"
                  strokeColor={{
                    from: '#108ee9',
                    to: '#87d068',
                  }}
                />
                <Text type="secondary">Seeding excise tax rules...</Text>
              </div>
            )}

            {/* Results */}
            {seedingComplete && seedingResults && (
              <Alert
                message="Seeding Complete"
                description={
                  <div>
                    <p><strong>Total:</strong> {seedingResults.total} rules</p>
                    <p><strong>Successful:</strong> {seedingResults.successful}</p>
                    <p><strong>Failed:</strong> {seedingResults.failed}</p>
                    {seedingResults.errors.length > 0 && (
                      <div>
                        <p><strong>Errors:</strong></p>
                        <ul>
                          {seedingResults.errors.map((error, index) => (
                            <li key={index}>{error}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                }
                type={seedingResults.failed === 0 ? "success" : "warning"}
                showIcon
                style={{ marginBottom: 24 }}
              />
            )}

            {/* Important Notes */}
            <Alert
              message="Important Notes"
              description={
                <ul>
                  <li>This will create real excise tax rules in your database</li>
                  <li>Rules are based on current 2024-2025 rates from official sources</li>
                  <li>Some rules (like Rhode Island) have future effective dates</li>
                  <li>Federal rules apply to all states automatically</li>
                  <li>You can manage these rules later in the Tax Rules section</li>
                </ul>
              }
              type="info"
              showIcon
              style={{ marginBottom: 24 }}
            />
          </Card>
        </Col>
      </Row>

      {/* Preview Modal */}
      <Modal
        title="Excise Tax Rules Preview"
        visible={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        footer={[
          <Button key="close" onClick={() => setPreviewVisible(false)}>
            Close
          </Button>
        ]}
        width={1200}
      >
        <div style={{ marginBottom: 16 }}>
          <Switch
            checked={selectedRules.size === exciseTaxRulesSeed.length}
            onChange={handleSelectAll}
            style={{ marginRight: 8 }}
          />
          <Text>Select All ({selectedRules.size} of {exciseTaxRulesSeed.length} selected)</Text>
        </div>
        
        <Table
          dataSource={exciseTaxRulesSeed}
          columns={columns}
          rowKey="id"
          pagination={{ pageSize: 10 }}
          size="small"
        />
      </Modal>
    </div>
  );
};

export default ExciseTaxSeed;